import React, { useEffect, useRef, useState } from 'react';
import { getAutoSuggestions } from '../services/chatbotApi';

function SuggestInput({ column, value, platform, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // Default parameter values are rendered on popup open, but must not trigger a search.
      if (!hasUserTyped || !platform || !column || !value || value.length < 2) { setSuggestions([]); setOpen(false); return; }
      const results = await getAutoSuggestions(platform, column, value);
      setSuggestions(results);
      setOpen(results.length > 0);
      setActiveIndex(-1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [value, column, platform, hasUserTyped]);

  useEffect(() => {
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const pick = (s) => {
    setHasUserTyped(false);
    onChange(s);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1)); }
    else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); pick(suggestions[activeIndex]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="modal-suggest-wrap" ref={wrapRef}>
      <input
        className="modal__input"
        value={value}
        onChange={(e) => { setHasUserTyped(true); onChange(e.target.value); }}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
        required
      />
      {open && suggestions.length > 0 && (
        <ul className="modal-suggest" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={i}
              role="option"
              aria-selected={i === activeIndex}
              className={`modal-suggest__item ${i === activeIndex ? 'is-active' : ''}`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function QuestionParametersModal({ question, onSubmit, onClose }) {
  const [parameters, setParameters] = useState(question.defaultParameters || {});
  const fields = question.editableFields?.length
    ? question.editableFields
    : Object.keys(question.dbFieldMap || {});
  const platform = question.platform;
  const dbFieldMap = question.dbFieldMap || {};
  const dbFieldFor = (parameter) => {
    if (dbFieldMap[parameter]) return dbFieldMap[parameter];
    const mapKey = Object.keys(dbFieldMap).find((key) => key.toLowerCase() === parameter.toLowerCase());
    return mapKey ? dbFieldMap[mapKey] : '';
  };
  const preview = (question.questionTemplate || question.displayQuestion || '').replace(/\{(\w+)\}/g, (_, key) => parameters[key] || `{${key}}`);

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal__head">
          <div className="modal__badge">✦ Question details</div>
          <button className="modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="modal__hint">Fill the fields needed for this question:</p>
        {fields.map((field) => {
          const dbField = dbFieldFor(field);
          return (
          <label className="modal-field" key={field}>
            {field.replace(/_/g, ' ')}
            <SuggestInput
              column={dbField}
              value={parameters[field] ?? ''}
              platform={platform}
              onChange={(val) => setParameters((curr) => ({ ...curr, [field]: val }))}
            />
          </label>
          );
        })}
        <div className="modal__preview"><span className="modal__preview-label">Preview:</span> {preview}</div>
        <div className="modal__actions">
          <button className="modal__cancel" onClick={onClose}>Cancel</button>
          <button className="modal__submit" onClick={() => onSubmit(parameters)}>Ask question</button>
        </div>
      </div>
    </div>
  );
}
